import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PencilIcon,
  UserGroupIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { useCourses } from '../../contexts/CourseContext';
import { ProgramClass } from '../../types';
import {
  getBatchClassApplicationStats,
  ClassApplicationStats
} from '../../services/programClassService';
import { supabase } from '../../lib/supabase';

type ClassStatusFilter = 'all' | 'recruiting' | 'upcoming' | 'completed';

interface ClassWithProgram extends ProgramClass {
  program_name: string;
  program_id: string;
}

const ClassManagement: React.FC = () => {
  const navigate = useNavigate();
  const { courses } = useCourses();
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState<ClassWithProgram[]>([]);
  const [classStats, setClassStats] = useState<Record<string, ClassApplicationStats>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ClassStatusFilter>('recruiting');

  // 반의 모집 상태를 계산
  const getClassStatus = (cls: ProgramClass): ClassStatusFilter => {
    const now = new Date();
    const startTime = cls.registration_start_time?.substring(0, 5) || '00:00';
    const endTime = cls.registration_end_time?.substring(0, 5) || '23:59';
    const startDateTime = new Date(`${cls.registration_start_date}T${startTime}:00+09:00`);
    const endDateTime = new Date(`${cls.registration_end_date}T${endTime}:00+09:00`);

    // 모집 종료 또는 교육 종료
    const courseEndDate = new Date(cls.course_end_date);
    if (now > courseEndDate) {
      return 'completed';
    }

    // 모집 중
    if (now >= startDateTime && now <= endDateTime) {
      return 'recruiting';
    }

    // 모집 예정 (모집 시작 전)
    if (now < startDateTime) {
      return 'upcoming';
    }

    // 모집 종료 후 교육 시작 전/진행 중
    return 'completed';
  };

  const getStatusLabel = (status: ClassStatusFilter) => {
    switch (status) {
      case 'upcoming': return '모집예정';
      case 'recruiting': return '모집중';
      case 'completed': return '종료';
      default: return '전체';
    }
  };

  const getStatusBadge = (status: ClassStatusFilter) => {
    switch (status) {
      case 'upcoming':
        return 'bg-amber-50 text-amber-700 border border-amber-200';
      case 'recruiting':
        return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'completed':
        return 'bg-slate-50 text-slate-600 border border-slate-200';
      default:
        return 'bg-indigo-50 text-indigo-700 border border-indigo-200';
    }
  };

  // 모든 반 로드
  const loadAllClasses = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('program_classes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading classes:', error);
        return;
      }

      // 프로그램 정보 매핑
      const classesWithProgram: ClassWithProgram[] = (data || []).map(cls => {
        const program = courses.find(c => c.id === cls.program_id);
        return {
          ...cls,
          program_name: program?.courseName || '알 수 없음',
          program_id: cls.program_id
        };
      });

      setClasses(classesWithProgram);

      // 모든 반의 신청 현황 로드
      if (classesWithProgram.length > 0) {
        const classIds = classesWithProgram.map(c => c.id);
        const stats = await getBatchClassApplicationStats(classIds);
        setClassStats(stats);
      }
    } catch (error) {
      console.error('Error loading classes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (courses.length > 0) {
      loadAllClasses();
    }
  }, [courses]);

  // 필터링
  const filteredClasses = classes.filter(cls => {
    const matchesSearch =
      cls.program_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cls.class_name.toLowerCase().includes(searchTerm.toLowerCase());

    const classStatus = getClassStatus(cls);
    const matchesStatus = statusFilter === 'all' || classStatus === statusFilter;

    return matchesSearch && matchesStatus && cls.is_published;
  });

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\. /g, '-').replace('.', '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-indigo-600 mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-1">반/모집 관리</h1>
        <p className="text-sm text-gray-500">
          실제 모집 단위인 반(Class)을 관리합니다. 각 반의 모집 현황을 확인하고 신청자를 관리할 수 있습니다.
        </p>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6 p-4">
        {/* 검색창 */}
        <div className="relative mb-4">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="프로그램명 또는 반 이름으로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-150"
          />
        </div>

        {/* 상태 필터 탭 */}
        <div className="flex gap-2">
          {(['all', 'recruiting', 'upcoming', 'completed'] as ClassStatusFilter[]).map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap ${
                statusFilter === status
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {getStatusLabel(status)}
              {status !== 'all' && (
                <span className="ml-2 px-1.5 py-0.5 bg-white bg-opacity-20 rounded text-xs">
                  {classes.filter(c => c.is_published && getClassStatus(c) === status).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* 반 목록 테이블 */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  프로그램명
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  반 이름
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  모집 상태
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  모집기간
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  교육기간
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  정원
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  신청
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  대기
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredClasses.map((cls) => {
                const stats = classStats[cls.id];
                const registeredCount = stats?.registered_count || 0;
                const waitingCount = stats?.waiting_count || 0;
                const classStatus = getClassStatus(cls);

                return (
                  <tr
                    key={cls.id}
                    className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                    onClick={() => navigate(`/admin/courses/${cls.program_id}`)}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {cls.program_name}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-indigo-600">
                        {cls.class_name}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(classStatus)}`}>
                        {getStatusLabel(classStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center whitespace-nowrap">
                      <div className="text-xs text-gray-600">
                        {formatDate(cls.registration_start_date)} ~<br />
                        {formatDate(cls.registration_end_date)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center whitespace-nowrap">
                      <div className="text-xs text-gray-600">
                        {formatDate(cls.course_start_date)} ~<br />
                        {formatDate(cls.course_end_date)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {cls.max_students}명
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center whitespace-nowrap">
                      <div className={`text-sm font-semibold ${
                        registeredCount >= cls.max_students
                          ? 'text-red-600'
                          : registeredCount >= cls.max_students * 0.8
                          ? 'text-amber-600'
                          : 'text-emerald-600'
                      }`}>
                        {registeredCount}명
                      </div>
                      <div className="text-xs text-gray-500">
                        ({Math.round((registeredCount / cls.max_students) * 100)}%)
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center whitespace-nowrap">
                      <div className={`text-sm font-semibold ${
                        waitingCount >= cls.waiting_list
                          ? 'text-red-600'
                          : waitingCount >= cls.waiting_list * 0.8
                          ? 'text-amber-600'
                          : 'text-emerald-600'
                      }`}>
                        {waitingCount}명
                      </div>
                      <div className="text-xs text-gray-500">
                        / {cls.waiting_list}명
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/courses/${cls.program_id}`);
                          }}
                          className="inline-flex items-center px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-150 font-medium"
                          title="신청자 보기"
                        >
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          신청자
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            alert('반 편집 기능은 프로그램 상세 페이지에서 이용하실 수 있습니다.');
                            navigate(`/admin/courses/${cls.program_id}`);
                          }}
                          className="inline-flex items-center p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-150 border border-gray-200"
                          title="편집"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredClasses.length === 0 && (
          <div className="text-center py-16">
            <p className="text-lg text-gray-500">
              {statusFilter === 'recruiting'
                ? '현재 모집 중인 반이 없습니다.'
                : '검색 결과가 없습니다.'}
            </p>
          </div>
        )}
      </div>

      {/* 요약 정보 */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs font-medium text-gray-500 mb-1">전체 반</div>
          <div className="text-2xl font-semibold text-gray-900">
            {classes.filter(c => c.is_published).length}개
          </div>
        </div>
        <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4">
          <div className="text-xs font-medium text-emerald-700 mb-1">모집 중</div>
          <div className="text-2xl font-semibold text-emerald-700">
            {classes.filter(c => c.is_published && getClassStatus(c) === 'recruiting').length}개
          </div>
        </div>
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
          <div className="text-xs font-medium text-amber-700 mb-1">모집 예정</div>
          <div className="text-2xl font-semibold text-amber-700">
            {classes.filter(c => c.is_published && getClassStatus(c) === 'upcoming').length}개
          </div>
        </div>
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
          <div className="text-xs font-medium text-slate-600 mb-1">종료</div>
          <div className="text-2xl font-semibold text-slate-600">
            {classes.filter(c => c.is_published && getClassStatus(c) === 'completed').length}개
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassManagement;
